/**
 * @file: This file writes the labels of a media from the Compose page.
 *
 * A label is data of the library, and Compose sets it at once (D14). D7 holds
 * the order of the playlist back until Save, and a label is not part of that
 * order.
 *
 * The catalogue of Compose reads the labels of a media from the list service
 * that holds it. A label is a row of a through table, and a through table is
 * not part of the state of the app, so a change of one reports nothing to that
 * service. This manager therefore listens for the change itself, reads the
 * labels of the media again, and writes them into the list service. That write
 * reports a state change, which rebuilds the catalogue and redraws every row of
 * the media.
 *
 * One path serves both cases. The operator sets a label here, or a colleague
 * sets one in the library. The server reports the row either way.
 *
 * The reports arrive one per row, and a bulk write in the library makes many.
 * The manager collects the media of a burst and reads each of them one time.
 */

import { mediaTraitsList, labelNamespaceOf } from '../../../../shared/media-types'

class LabelWriter {
  constructor (controller) {
    this.controller = controller
    // The list service that holds the media of each type, and its labels.
    this._servicesByType = {
      file: controller._FileMediaService,
      social: controller._SocialMediaService,
      serviceAnnouncement: controller._ServiceAnnouncementsService
    }
    this._listeners = []
    // The media of the current burst, by namespace and id, so one media is read
    // one time however many of its rows changed.
    this._touched = new Map()
    this._flushQueued = false
  }

  /** Listen for a change of any label row. */
  attach () {
    this._listeners = mediaTraitsList().map(traits => ({
      namespace: traits.labelNamespace,
      uuid: this._labels().subscribeToLabelRelationChanges(
        traits.labelNamespace, relation => this._relationChanged(traits, relation))
    }))
  }

  /** Give up the listeners. Called when the page goes away. */
  detach () {
    this._listeners.forEach(listener =>
      this._labels().unsubscribeToLabelRelationChanges(listener.namespace, listener.uuid))
    this._listeners = []
    this._touched.clear()
    this._flushQueued = false
  }

  // ===== Queries =====

  /** Every label of the app, for the entries of the menu. */
  allLabels () {
    return this._labels().getAll()
  }

  /**
   * The labels of one media, read from the server.
   *
   * The menu reads them on every right-click. The catalogue can be behind, and
   * it does not hold a media at all when the row is older than the library.
   *
   * @param {object} media - The media of the row
   * @returns {Promise} The labels, or an empty list for a type with no labels
   */
  labelsOf (media) {
    const namespace = labelNamespaceOf(media.mediaType)

    if (!namespace) {
      return this.controller._$q.when([])
    }

    return this.controller._$q.when(this._labels().getLabels(namespace, media.mediaId))
  }

  // ===== Commands =====

  /**
   * Add a label to a media, or take it away.
   *
   * The write starts inside the chain, so a refusal that the service throws at
   * once lands in the same place as a refusal from the server.
   *
   * @param {object} media - The media of the row
   * @param {object} label - The label the operator chose
   * @param {boolean} wasChecked - Whether the media had the label
   * @returns {Promise} The write
   */
  toggle (media, label, wasChecked) {
    const namespace = labelNamespaceOf(media.mediaType)

    if (!namespace) {
      return this.controller._$q.when()
    }

    return this.controller._$q.when()
      .then(() => (wasChecked
        ? this._labels().removeLabel(namespace, media.mediaId, label.id)
        : this._labels().addLabel(namespace, media.mediaId, label.id)))
      .catch(error => this._failed(error))
  }

  // ===== Internals =====

  _labels () {
    return this.controller._MediaLabelService
  }

  /**
   * One row of a through table changed. The row names its media in the field
   * the shared traits give.
   */
  _relationChanged (traits, relation) {
    const mediaId = relation && relation[traits.labelIdField]

    if (!mediaId) {
      return
    }

    this._touched.set(`${traits.labelNamespace}:${mediaId}`, { traits, mediaId })

    if (this._flushQueued) {
      return
    }

    this._flushQueued = true
    // The reports arrive outside Angular, and the read runs once for the burst.
    this.controller._$scope.$applyAsync(() => this._flush())
  }

  /** Read the labels of every media of the burst and hand them to its service. */
  _flush () {
    const touched = [...this._touched.values()]
    this._touched.clear()
    this._flushQueued = false

    return this.controller._$q.all(touched.map(one => this._refresh(one.traits, one.mediaId)))
  }

  _refresh (traits, mediaId) {
    const service = this._servicesByType[traits.mediaType]

    if (!service) {
      return this.controller._$q.when()
    }

    return this.controller._$q
      .when(this._labels().getLabels(traits.labelNamespace, mediaId))
      .then(labels => service.updatePropertyLocally(mediaId, 'labels', labels))
      .catch(error => this.controller._$log
        .error(`[PlaylistCompose] labels of ${traits.labelNamespace} ${mediaId}: ${error.message}`, error))
  }

  _failed (error) {
    this.controller._$log.error(`[PlaylistCompose] label: ${error.message}`, error)
    this.controller._toaster.error(error.message, 'Could not change the label')
  }
}

export { LabelWriter }
